/**
 * usePayments
 * React Query hook for fetching payment data for approved pots
 */

import { useQuery, useMutation } from '@tanstack/react-query';
import {
  getPayments,
  downloadPaymentsCsv,
  PaymentRowDto,
} from '../api/paymentsApi';
import { useAuthStore } from '../../auth/stores/authStore';

// Query keys for payments
export const paymentsKeys = {
  all: ['payments'] as const,
  list: (potPeriodId: string) =>
    [...paymentsKeys.all, 'list', potPeriodId] as const,
};

export interface UsePaymentsReturn {
  payments: PaymentRowDto[];
  periodStart: string | null;
  periodEnd: string | null;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Hook to fetch payment data for approved pots in a period
 *
 * @param potPeriodId - The pot period ID to fetch payments for
 */
export function usePayments(potPeriodId: string | null): UsePaymentsReturn {
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);

  const { data, isLoading, error } = useQuery({
    queryKey: paymentsKeys.list(potPeriodId ?? ''),
    queryFn: async () => {
      if (!potPeriodId) {
        throw new Error('No pot period selected');
      }
      return getPayments(potPeriodId);
    },
    enabled: isAuthenticated && !!potPeriodId,
    staleTime: 30000, // 30 seconds
  });

  return {
    payments: data?.payments ?? [],
    periodStart: data?.periodStart ?? null,
    periodEnd: data?.periodEnd ?? null,
    isLoading,
    error: error as Error | null,
  };
}

/**
 * Hook to download payments CSV
 */
export function useDownloadPaymentsCsv() {
  return useMutation({
    mutationFn: (potPeriodId: string) => downloadPaymentsCsv(potPeriodId),
  });
}
